import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Device } from '@ionic-native/device/ngx';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  APP_CONFIG,
  SettingsService,
} from 'src/app/services/settings.service';
import {
  ConnectionStatus,
  NetworkService,
} from 'src/app/services/network.service';
import { TenantAuthService } from 'src/app/services/tenant-auth.service';
import { TerminalDataService } from 'src/app/private/terminal/services/terminal-data.service';
import { TerminalStoreService } from 'src/app/private/terminal/services/terminal-store.service';
import DateUtils from 'src/app/utility/date-utils';

const DEFAULT_INTERVAL_MS = 30_000;
const MIN_INTERVAL_MS = 5_000;

export interface ITerminalHealthReport {
  imei: string;
  platform: string;
  platformVersion?: string;
  model?: string;
  manufacturer?: string;
  serial?: string;
  appVersion: string;
  version: string;
  online: boolean;
  companyId?: string;
  storeId?: number;
  checkedAt: string;
}

export interface ITerminalHealthStatus {
  report: ITerminalHealthReport;
  ok: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class TerminalHealthService {
  private _http: HttpClient;
  private _pollSub: Subscription;
  private _inFlight = false;
  private _status$ = new BehaviorSubject<ITerminalHealthStatus | null>(null);

  readonly status$: Observable<ITerminalHealthStatus | null> =
    this._status$.asObservable();

  get status(): ITerminalHealthStatus | null {
    return this._status$.value;
  }

  get isStarted(): boolean {
    return !!this._pollSub;
  }

  constructor(
    httpBackend: HttpBackend,
    private _settings: SettingsService,
    private _device: Device,
    private _appVersion: AppVersion,
    private _network: NetworkService,
    private _tenantAuth: TenantAuthService,
    private _terminalData: TerminalDataService,
    private _terminalStore: TerminalStoreService,
    @Inject('ENVIRONMENT') private environment: { version: string }
  ) {
    this._http = new HttpClient(httpBackend);
  }

  start() {
    if (this._pollSub) {
      return;
    }

    this._pollSub = this._settings.config
      .pipe(
        map(() => this._resolveIntervalMs()),
        distinctUntilChanged(),
        switchMap((intervalMs) => timer(0, intervalMs))
      )
      .subscribe(() => this._poll());
  }

  stop() {
    if (this._pollSub) {
      this._pollSub.unsubscribe();
      this._pollSub = null;
    }
  }

  pollNow() {
    return this._poll();
  }

  private async _poll() {
    if (this._inFlight) {
      return;
    }

    this._inFlight = true;
    let report: ITerminalHealthReport;
    try {
      report = await this._buildReport();
      await this._sendReport(report).toPromise();
      this._status$.next({ report, ok: true });
    } catch (err) {
      console.error(err, { service: 'TerminalHealthService' });
      this._status$.next({
        report:
          report ||
          this._status$.value?.report ||
          ({} as ITerminalHealthReport),
        ok: false,
        error: this._errorMessage(err),
      });
    } finally {
      this._inFlight = false;
    }
  }

  private _sendReport(report: ITerminalHealthReport): Observable<void> {
    return this._http.put<void>(
      `${this._settings.pos_api_endpoint}/terminal/${encodeURIComponent(
        report.imei
      )}/health`,
      report
    );
  }

  private async _buildReport(): Promise<ITerminalHealthReport> {
    const appVersion = await this._getAppVersion();

    return {
      imei: this._device.uuid,
      platform: this._device.platform,
      platformVersion: this._device.version,
      model: this._device.model,
      manufacturer: this._device.manufacturer || '',
      serial: this._device.serial,
      appVersion,
      version: appVersion,
      online: this._network.getCurrentNetworkStatus() === ConnectionStatus.Online,
      companyId: this._tenantAuth.tenantId,
      storeId: this._getStoreId(),
      checkedAt: DateUtils.format(),
    };
  }

  private async _getAppVersion(): Promise<string> {
    if (this._device.platform == 'web_browser') {
      return this.environment.version;
    }

    return this._appVersion
      .getVersionNumber()
      .catch(() => this.environment.version);
  }

  private _getStoreId(): number | undefined {
    const settings = this._terminalData.terminalSettings();
    if (!settings?.store) {
      return undefined;
    }

    const selectedStoreId = this._terminalStore.getTerminalStoreId();
    const store = selectedStoreId
      ? this._findStore(settings.store, selectedStoreId)
      : settings.store;

    if (!store) {
      return undefined;
    }

    const storeId = store.id != null ? Number(store.id) : NaN;
    return Number.isNaN(storeId) ? undefined : storeId;
  }

  private _findStore(store: any, storeId: number): any {
    if (Number(store.id) === Number(storeId)) {
      return store;
    }

    return (store.departments || []).find(
      (department: any) => Number(department.id) === Number(storeId)
    );
  }

  private _resolveIntervalMs(): number {
    const value = Number(
      this._settings.getValue(APP_CONFIG.TERMINAL_HEALTH_INTERVAL)
    );
    if (!value || Number.isNaN(value) || value < MIN_INTERVAL_MS) {
      return DEFAULT_INTERVAL_MS;
    }

    return value;
  }

  private _errorMessage(err: any): string {
    if (!err) {
      return 'Unknown error';
    }

    if (typeof err === 'string') {
      return err;
    }

    return err.message || err.statusText || 'Health poll failed';
  }
}
